import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import * as qs from 'qs';
import { useDispatch } from 'react-redux';
import V2Layout from './components/V2Layout';
import V2Icon from './components/V2Icon';
import V2PortableLayout from './components/V2PortableLayout';
import useEffectivePortableMode from '../hooks/useEffectivePortableMode';
import V2PlayerSearch from './components/V2PlayerSearch';
import V2GlobalFilters from './components/V2GlobalFilters';
import V2ChartSection from './components/V2ChartSection';
import V2SummarySection from './components/V2SummarySection';
import { readChartPrefs, type ChartPosition } from './utils/chartPrefs';
import V2PostflopPanel from './components/V2PostflopPanel';
import useV2Auth from './hooks/useV2Auth';
import useV2Stats from './hooks/useV2Stats';
import useSearchPlayers from '../hooks/useSearchPlayers';
import { findStats } from '../containers/StatPage/actions';
import { getPokersiteIdByPokersiteName, pokerSitePostfix } from '../containers/StatPage/utils';
import formatHands from '../utils/formatHands';
import formatWinnings from '../utils/formatWinnings';
import type { StatView } from './data/demoStats';
function getTextNameOfLimit(limit: number) {
  return `NL${limit}`;
}

function getTextNameOfTableSize(tableSize: 2 | 6 | 9) {
  if (tableSize === 2) return 'HU';
  if (tableSize === 6) return 'SH';
  return 'FR';
}

const V2Statistics: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const usePortableLayout = useEffectivePortableMode();
  const { user, initialLoad } = useV2Auth();
  const {
    player,
    generalStats,
    graphResults,
    graphLoading,
    monthStats,
    tableSizeStats,
    limitStats,
    showMiningError,
    filterSummary,
    resetFilters,
  } = useV2Stats();

  const {
    nicknameToFind,
    searchPlayer,
    focusPlayerSearch,
    blurPlayerSearch,
    options,
    selectPlayer,
    clearAutocompletePlayers,
  } = useSearchPlayers();
  const [view, setView] = React.useState<StatView>('overview');
  const [chartPosition, setChartPosition] = React.useState<ChartPosition>(
    () => readChartPrefs().position,
  );
  const [compact, setCompact] = React.useState(
    () => new URLSearchParams(window.location.search).get('density') === 'compact',
  );
  React.useEffect(() => {
    if (!initialLoad || !user) {
      return;
    }

    const params = qs.parse(location.search, { ignoreQueryPrefix: true });
    if (params.playername && params.room) {
      const pokersiteId = getPokersiteIdByPokersiteName(params.room as string);
      if (pokersiteId) {
        dispatch(
          findStats({
            playername: params.playername as string,
            pokersiteId,
          }),
        );
      }
    }
  }, [dispatch, location.search, initialLoad, user]);

  const handleResetFilters = () => {
    resetFilters();
  };

  const searchOptions = options.map((option) => ({
    id: option.value,
    label: option.player.playername,
    subtitle: pokerSitePostfix[option.player.pokersiteId],
    href: option.href,
  }));

  if (!initialLoad) {
    return null;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (usePortableLayout) {
    return <V2PortableLayout />;
  }

  if (showMiningError) {
    return (
      <V2Layout
        activeNav="players"
        userEmail={user?.email}
        showWorkspaceTools
        footerNote="Ошибка доступа"
      >
        <section className="empty-state">
          <span aria-hidden="true">!</span>
          <h2>Нет доступа к данным</h2>
          <p>
            Для этого рума нужна подписка или майнинг. Свяжитесь с поддержкой или перейдите в{' '}
            <Link to="/subscription">тарифы</Link>.
          </p>
          <a
            className="acid-button"
            href="https://t.me/fishh_support"
            target="_blank"
            rel="noreferrer"
          >
            Написать в поддержку ↗
          </a>
        </section>
      </V2Layout>
    );
  }

  if (!player) {
    return (
      <V2Layout
        activeNav="players"
        userEmail={user?.email}
        showSearch
        showWorkspaceTools
        searchQuery={nicknameToFind ?? ''}
        onSearchQueryChange={searchPlayer}
        onSearchFocus={focusPlayerSearch}
        onSearchBlur={blurPlayerSearch}
        searchOptions={searchOptions}
        onSelectSearchOption={selectPlayer}
        footerNote="Поиск игрока"
      >
        <section className="empty-state empty-state--search">
          <span aria-hidden="true">
            <V2Icon name="search" />
          </span>
          <h2>Найдите игрока</h2>
          <p>
            Введите ник и выберите игрока из списка. Можно также искать через поле в шапке или
            клавишу /.
          </p>
          <V2PlayerSearch
            inputId="search-player-hero"
            variant="hero"
            autoFocus
            query={nicknameToFind ?? ''}
            onQueryChange={searchPlayer}
            onFocus={focusPlayerSearch}
            onBlur={blurPlayerSearch}
            options={searchOptions}
            onSelectOption={selectPlayer}
            emptyNote="Игрок не найден. Проверьте написание ника."
          />
        </section>
      </V2Layout>
    );
  }

  const stats = generalStats ?? {};
  const winningsType = stats.winnings == null ? '' : stats.winnings >= 0 ? 'positive' : 'negative';
  const hasHands = (stats.hands ?? 0) > 0;

  const preflopPairs: Array<[string, string | number]> = [
    ['3bet', stats.threebet ?? '—'],
    ['F3bet', stats.foldedtothreebetpreflop ?? '—'],
    ['4bet', stats.raisedthreebetpreflop ?? '—'],
    ['F4bet', stats.foldedtofourbetpreflop ?? '—'],
    ['5bet', stats.raisedfourbetpreflop ?? '—'],
    ['SQZ', stats.squeeze ?? '—'],
    ['CC', stats.coldcall ?? '—'],
    ['WTSD', stats.sawshowdown ?? '—'],
    ['WWSF', stats.wonwhensawflop ?? '—'],
    ['W$SD', stats.wonshowdown ?? '—'],
  ];

  const chartSection = (
    <V2ChartSection
      hands={stats.hands ?? 0}
      periodLabel={filterSummary[2].toLowerCase()}
      winnings={stats.winnings}
      evWinnings={stats.evWinnings}
      evBb100={stats.evBb100}
      rlBb100={stats.rlBb100}
      graph={graphResults}
      loading={graphLoading}
      position={chartPosition}
      onPositionChange={setChartPosition}
    />
  );

  const preflopStrip =
    view === 'overview' ? (
      <section className="stats-row stats-row--preflop" aria-label="Preflop">
        {preflopPairs.map(([name, value]) => (
          <div className="stat-cell" key={name}>
            <p className="stat-cell__label">{name}</p>
            <p className="stat-cell__value">{value}</p>
          </div>
        ))}
      </section>
    ) : null;

  const statsPanels = (
    <div className="stats-panels stats-panels--postflop">
      <V2PostflopPanel stats={stats} />
    </div>
  );

  const summaryRows = [
    ...tableSizeStats.map((row) => ({
      key: getTextNameOfTableSize(row.tableSize as 2 | 6 | 9),
      hands: formatHands(row.hands, 1),
      winnings: formatWinnings(row.winnings, 1, false),
      winningsClass: row.winnings >= 0 ? 'positive' : 'negative',
      bb100: row.bb100 ?? '—',
      vpip: row.vpip ?? '—',
      pfr: row.pfr ?? '—',
      wwsf: row.wonwhensawflop ?? '—',
    })),
    ...limitStats.map((row) => ({
      key: getTextNameOfLimit(row.limit),
      hands: row.hands == null ? '—' : formatHands(row.hands, 1),
      winnings: row.winnings == null ? '—' : formatWinnings(row.winnings, 1, false),
      winningsClass: row.winnings == null ? '' : row.winnings >= 0 ? 'positive' : 'negative',
      bb100: row.bb100 ?? '—',
      vpip: row.vpip ?? '—',
      pfr: row.pfr ?? '—',
      wwsf: row.wonwhensawflop ?? '—',
    })),
  ];

  const summarySection = <V2SummarySection rows={summaryRows} />;

  const overviewContent = (
    <>
      {chartPosition === 'top' ? chartSection : statsPanels}
      {chartPosition === 'top' ? statsPanels : chartSection}
      {summarySection}
    </>
  );

  const historyContent =
    monthStats.length === 0 ? (
      <p className="table-hint">Нет данных по месяцам для выбранных фильтров.</p>
    ) : (
      <section className="history-section">
        <div className="block-heading">
          <h2>История результатов</h2>
          <span className="muted">По месяцам</span>
        </div>
        <div className="table-scroll" tabIndex={0} role="region" aria-label="История результатов">
          <table className="history-table">
            <thead>
              <tr>
                {['Период', 'Раздачи', 'VPIP / PFR', 'bb/100', 'Результат'].map((title) => (
                  <th key={title} scope="col">
                    {title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {monthStats.slice(0, 12).map((row) => (
                <tr key={row.month}>
                  <td>{row.month}</td>
                  <td>{formatHands(row.hands, 1)}</td>
                  <td>
                    {row.vpip ?? '—'} / {row.pfr ?? '—'}
                  </td>
                  <td>{row.bb100 ?? '—'}</td>
                  <td className={row.winnings >= 0 ? 'positive' : 'negative'}>
                    {formatWinnings(row.winnings, 1, false)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );

  const summaryContent = summarySection;

  const statContent = !hasHands ? (
    <section className="empty-state">
      <span aria-hidden="true">∅</span>
      <h2>Пока нет раздач</h2>
      <p>
        За выбранный период или фильтры данных нет. Попробуйте расширить период или сбросить
        фильтры.
      </p>
      <button type="button" className="acid-button" onClick={handleResetFilters}>
        Сбросить фильтры ↗
      </button>
    </section>
  ) : (
    <>
      <div className="stats-summary">
        <section className="stats-row stats-row--primary" aria-label="Ключевые показатели">
          <div className="stat-cell">
            <p className="stat-cell__label">VPIP / PFR</p>
            <p className="stat-cell__value stat-cell__value--ratio">
              {stats.vpip ?? '—'}
              <span className="kpi-ratio-sep" aria-hidden="true">
                /
              </span>
              {stats.pfr ?? '—'}
            </p>
          </div>
          <div className="stat-cell">
            <p className="stat-cell__label">Total winnings</p>
            <p className={`stat-cell__value ${winningsType}`}>
              {stats.winnings == null ? '—' : formatWinnings(stats.winnings, 1, true)}
            </p>
          </div>
          <div className="stat-cell">
            <p className="stat-cell__label">Winrate</p>
            <p className="stat-cell__value">
              {stats.bb100 ?? '—'}
              <small>bb/100</small>
            </p>
          </div>
          <div className="stat-cell">
            <p className="stat-cell__label">Hands / раздачи</p>
            <p className="stat-cell__value">
              {formatHands(stats.hands ?? 0, 1)}
              <small>раздач</small>
            </p>
          </div>
        </section>
        {preflopStrip}
      </div>
      {view === 'overview' ? overviewContent : view === 'history' ? historyContent : summaryContent}
    </>
  );

  return (
    <V2Layout
      activeNav="players"
      compact={compact}
      onToggleCompact={() => setCompact((value) => !value)}
      showSearch
      showWorkspaceTools
      userEmail={user?.email}
      searchQuery={nicknameToFind ?? ''}
      onSearchQueryChange={searchPlayer}
      onSearchFocus={focusPlayerSearch}
      onSearchBlur={blurPlayerSearch}
      onSearchSubmit={() => undefined}
      searchOptions={searchOptions}
      onSelectSearchOption={(id) => {
        selectPlayer(id);
        clearAutocompletePlayers();
      }}
      breadcrumbs={
        <p className="breadcrumbs">
          Игроки <span>/</span> {player.pokersitePostfix} <span>/</span>{' '}
          <strong>{player.playername}</strong>
        </p>
      }
      footerNote={player.playername}
    >
      <section className="player-heading">
        <div>
          <div className="player-title-line">
            <h1>{player.playername}</h1>
            <span className="room-chip acid">{player.pokersitePostfix}</span>
          </div>
          {player.sameNickPlayers.length > 0 ? (
            <p className="profile-description">
              <span className="demo-label">
                Также:{' '}
                {player.sameNickPlayers.map((samePlayer, index) => (
                  <React.Fragment key={samePlayer.id}>
                    {index > 0 ? ', ' : ''}
                    <button
                      type="button"
                      className="text-link"
                      onClick={() =>
                        dispatch(
                          findStats({
                            playername: samePlayer.playername,
                            pokersiteId: samePlayer.pokersite_id,
                          }),
                        )
                      }
                    >
                      {pokerSitePostfix[samePlayer.pokersite_id]}
                    </button>
                  </React.Fragment>
                ))}
              </span>
            </p>
          ) : null}
        </div>
        <V2GlobalFilters />
      </section>

      <div className="view-row">
        <nav className="view-tabs" aria-label="Представления статистики">
          {(['overview', 'history', 'summary'] as StatView[]).map((tab) => (
            <button
              key={tab}
              type="button"
              aria-current={view === tab ? 'page' : undefined}
              onClick={() => setView(tab)}
            >
              {tab === 'overview' ? 'Обзор' : tab === 'history' ? 'История' : 'Summary'}
            </button>
          ))}
        </nav>
      </div>

      <div id="stat-content" aria-live="polite">
        {statContent}
      </div>
    </V2Layout>
  );
};

export default V2Statistics;
